import { IdbPersistenceFileDb } from './files/IdbPersistenceFileDb.js';
import { IdbMetadataDb } from './metadata/IdbMetadataDb.js';
import { openMetadataDatabase } from './metadata/openMetadataDatabase.js';
import { IdbDocumentDb } from './queries/IdbDocumentDb.js';
import { closeDatabase, deleteDatabase, getDocumentDbName, getMetadataDbName, getNamespaceFromDatabaseInfo, overwriteDatabase, } from './util.js';
import { openDatabase, upgradeDatabase } from './queries/migration/db.js';
export class IdbPersistence {
    constructor(indexedDB = window.indexedDB) {
        this.indexedDB = indexedDB;
        this.name = 'IdbPersistence';
        this.getNamespaces = async () => {
            // list all idb database names
            const dbs = await this.indexedDB.databases();
            return Array.from(new Set(dbs.map(getNamespaceFromDatabaseInfo).filter((n) => !!n)));
        };
        this.getNamespaceVersion = async (namespace) => {
            var _a;
            const databaseName = getDocumentDbName(namespace);
            const dbInfo = await this.indexedDB.databases();
            const existingDb = dbInfo.find((info) => info.name === databaseName);
            if (existingDb) {
                return (_a = existingDb.version) !== null && _a !== void 0 ? _a : 0;
            }
            return 0;
        };
        this.deleteNamespace = async (namespace, ctx) => {
            await Promise.all([
                deleteDatabase(getMetadataDbName(namespace), this.indexedDB),
                deleteDatabase([namespace, 'collections'].join('_'), this.indexedDB),
            ]);
        };
        this.openNamespace = async (namespace) => {
            return new IdbPersistenceNamespace(this.indexedDB, namespace);
        };
        this.copyNamespace = async (from, to, ctx) => {
            const fromCtx = Object.assign(Object.assign({}, ctx), { namespace: from });
            const toCtx = Object.assign(Object.assign({}, ctx), { namespace: to });
            const { db: fromMetaDb } = await openMetadataDatabase({
                indexedDB: this.indexedDB,
                log: fromCtx.log,
                namespace: fromCtx.namespace,
            });
            // no need to involve files, as they store all data
            // in the metadata database.
            const fromDocumentsDb = await openDatabase({
                indexedDB: this.indexedDB,
                namespace: fromCtx.namespace,
                version: fromCtx.schema.version,
                log: fromCtx.log,
            });
            fromCtx.log('info', `Copying data from ${fromCtx.namespace} to ${toCtx.namespace}`);
            await overwriteDatabase(fromMetaDb, getMetadataDbName(toCtx.namespace), toCtx, this.indexedDB);
            await overwriteDatabase(fromDocumentsDb, getDocumentDbName(toCtx.namespace), toCtx, this.indexedDB);
            await closeDatabase(fromMetaDb);
            await closeDatabase(fromDocumentsDb);
        };
    }
}
class IdbPersistenceNamespace {
    constructor(indexedDB, namespace) {
        this.indexedDB = indexedDB;
        this.namespace = namespace;
        this.openMetadata = async (ctx) => {
            const { db } = await openMetadataDatabase({
                indexedDB: this.indexedDB,
                log: ctx.log,
                namespace: this.namespace,
            });
            this.metadataDb = db;
            ctx.persistenceShutdownHandler.register(() => closeDatabase(db));
            return new IdbMetadataDb(db, ctx);
        };
        this.openDocuments = async (ctx) => {
            const db = await openDatabase({
                version: ctx.schema.version,
                indexedDB: this.indexedDB,
                log: ctx.log,
                namespace: this.namespace,
            });
            ctx.persistenceShutdownHandler.register(() => closeDatabase(db));
            return new IdbDocumentDb(db, ctx);
        };
        this.applyMigration = async (ctx, migration) => {
            ctx.log('debug', 'Applying migration', migration.newSchema.version, migration);
            await upgradeDatabase(this.indexedDB, this.namespace, migration.newSchema.version, (transaction, db) => {
                for (const newCollection of migration.addedCollections) {
                    db.createObjectStore(newCollection, {
                        keyPath: migration.newSchema.collections[newCollection].primaryKey,
                        autoIncrement: false,
                    });
                }
                for (const collection of migration.allCollections) {
                    const store = transaction.objectStore(collection);
                    // apply new indexes
                    for (const newIndex of migration.addedIndexes[collection] || []) {
                        store.createIndex(newIndex.name, newIndex.name, {
                            multiEntry: newIndex.multiEntry,
                        });
                    }
                    // remove old indexes
                    for (const oldIndex of migration.removedIndexes[collection] || []) {
                        store.deleteIndex(oldIndex.name);
                    }
                }
                for (const removedCollection of migration.removedCollections) {
                    // !! can't delete the store, because old operations that relate to
                    // this store may still exist in history. instead, we can clear it out
                    // and leave it in place
                    transaction.objectStore(removedCollection).clear();
                }
            }, ctx.log);
        };
    }
    openFiles(ctx) {
        if (!this.metadataDb) {
            throw new Error('Metadata database must be opened first. This is a bug in Verdant.');
        }
        return Promise.resolve(new IdbPersistenceFileDb(this.metadataDb, ctx));
    }
}
//# sourceMappingURL=idbPersistence.js.map